import { VolumeHierarchyManager } from '../../../mol-plugin-state/manager/volume/hierarchy';
import { PluginContext } from '../../../mol-plugin/context';
import { Box3D } from '../../../mol-math/geometry';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { Grid, Volume } from '../../../mol-model/volume';
import { StructureElement, Unit } from '../../../mol-model/structure';
import { residueFromLocation } from '../identity';
import { failure as rawFailure, pagedResult, success, toMcpResult } from '../result';
import { ToolDefinition, ToolSchema, WebMcpRequest, WebMcpValue } from '../registration';
import { createSelectorResolverContext, resolveSelector, selectorPropertiesRequestSchema } from '../selector';
import { Residue } from '../types';
import { WebMcpSafetyServices, runWebMcpMutation } from './safety';

interface VolumeState { readonly ref: string, readonly label: string, readonly representations: readonly { readonly ref: string, readonly type: string }[] }
interface DensityFitInput { readonly target: WebMcpValue, readonly volumeRef?: string, readonly threshold: number }
interface DensityResidueSummary extends Residue { readonly atomCount: number, readonly mean: number, readonly min: number, readonly max: number }
interface DensityAccumulator { readonly residue: Residue, count: number, sum: number, min: number, max: number }
type VolumeIsovalueKind = 'sigma' | 'absolute';

export const VolumeRepresentationSchema: ToolSchema = { type: 'object', properties: { ref: { type: 'string', minLength: 1 }, type: { type: 'string', minLength: 1 } }, required: ['ref', 'type'], additionalProperties: false };
export const DensityFitSchema = selectorPropertiesRequestSchema(['target'], { volumeRef: { type: 'string', minLength: 1 }, threshold: { type: 'number' } });
export const VolumeIsovalueSchema: ToolSchema = { type: 'object', properties: { representationRef: { type: 'string', minLength: 1 }, kind: { type: 'string', enum: ['sigma', 'absolute'] }, value: { type: 'number', minimum: -1000000, maximum: 1000000 } }, required: ['representationRef', 'kind', 'value'], additionalProperties: false };

function isString(value: WebMcpValue | undefined): value is string { return typeof value === 'string'; }
function isNumber(value: WebMcpValue | undefined): value is number { return typeof value === 'number' && Number.isFinite(value); }
function failure(error: string, hint: string) { return toMcpResult(rawFailure(error, hint)); }

function parseDensityFitInput(input: WebMcpRequest): DensityFitInput | undefined {
    if (input.target === void 0 || (input.volumeRef !== void 0 && !isString(input.volumeRef)) || (input.threshold !== void 0 && !isNumber(input.threshold))) return void 0;
    return { target: input.target, volumeRef: input.volumeRef, threshold: input.threshold ?? 0 };
}

function volumes(plugin: PluginContext): readonly VolumeState[] {
    return plugin.managers.volume.hierarchy.current.volumes.map(volume => ({
        ref: volume.cell.transform.ref,
        label: volume.cell.obj?.label ?? volume.cell.transform.ref,
        representations: volume.representations.flatMap(representation => representation.cell.transform.params ? [{ ref: representation.cell.transform.ref, type: representation.cell.transform.params.type.name }] : []),
    }));
}

function selectVolume(plugin: PluginContext, ref: string | undefined) {
    const available = plugin.managers.volume.hierarchy.current.volumes;
    if (ref !== void 0) return available.find(entry => entry.cell.transform.ref === ref);
    return available.length === 1 ? available[0] : void 0;
}

function isIsovalueKind(value: WebMcpValue | undefined): value is VolumeIsovalueKind { return value === 'sigma' || value === 'absolute'; }

export function adjustedVolumeIsoValue(volume: Volume, kind: VolumeIsovalueKind, value: number) {
    return Volume.adjustedIsoValue(volume, value, kind === 'sigma' ? 'relative' : 'absolute');
}

async function setVolumeIsovalue(plugin: PluginContext, representationRef: string, kind: VolumeIsovalueKind, value: number) {
    for (const volume of plugin.managers.volume.hierarchy.current.volumes) {
        const representation = volume.representations.find(entry => entry.cell.transform.ref === representationRef);
        if (!representation) continue;
        const data = volume.cell.obj?.data;
        const params = representation.cell.transform.params;
        if (!data || !params) throw new Error(`Representation '${representationRef}' has no volume data or parameters.`);
        if (params.type.name !== 'isosurface') throw new Error(`Representation '${representationRef}' is not an isosurface.`);
        const isoValue = adjustedVolumeIsoValue(data, kind, value);
        const update = plugin.state.data.build().to(representation.cell).update({
            ...params,
            type: { ...params.type, params: { ...params.type.params, isoValue } }
        });
        await update.commit({ canUndo: 'Set Volume Isovalue' });
        return;
    }
    throw new Error(`Representation '${representationRef}' is unavailable.`);
}

function residueAt(ref: string, location: StructureElement.Location): Residue | undefined {
    if (!Unit.isAtomic(location.unit)) return void 0;
    return residueFromLocation(ref, location);
}

function densityFit(plugin: PluginContext, input: DensityFitInput, services: WebMcpSafetyServices) {
    const volume = selectVolume(plugin, input.volumeRef);
    if (!volume) {
        const available = plugin.managers.volume.hierarchy.current.volumes;
        return available.length === 0
            ? failure('No volume is loaded.', 'Load a density volume, then retry.')
            : failure(input.volumeRef === void 0 ? 'A volume ref is required when multiple volumes are loaded.' : `Volume '${input.volumeRef}' is not loaded.`, 'Call get-volume-state and provide one loaded volume ref.');
    }
    const data = volume.cell.obj?.data;
    if (!data) return failure(`Volume '${volume.cell.transform.ref}' is unavailable.`, 'Reload the volume, then retry.');
    if (data.grid.stats.sigma === 0) return failure('Density fit requires a volume with non-zero sigma.', 'Load a volume containing varying density values.');
    const matched = resolveSelector(input.target, createSelectorResolverContext(plugin));
    if (!matched.ok) return toMcpResult(matched);

    const interpolate = Grid.makeGetTrilinearlyInterpolated(data.grid, 'relative');
    const position = Vec3();
    const residues = new Map<string, DensityAccumulator>();
    let count = 0;
    let sum = 0;
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let below = 0;
    for (const entry of matched.entries) StructureElement.Loci.forEachLocation(entry.loci, location => {
        const residue = residueAt(entry.ref, location);
        if (!residue) return;
        location.unit.conformation.position(location.element, position);
        const value = interpolate(position);
        if (!Number.isFinite(value)) return;
        count++;
        sum += value;
        min = Math.min(min, value);
        max = Math.max(max, value);
        if (value < input.threshold) below++;
        const key = `${residue.id}/${residue.insCode ?? ''}`;
        const accumulator = residues.get(key);
        if (accumulator) {
            accumulator.count++;
            accumulator.sum += value;
            accumulator.min = Math.min(accumulator.min, value);
            accumulator.max = Math.max(accumulator.max, value);
        } else {
            residues.set(key, { residue, count: 1, sum: value, min: value, max: value });
        }
    });
    if (count === 0) return failure('The selected atoms are outside the volume grid.', 'Choose atoms overlapping the loaded volume, or select another volume.');
    const summaries: DensityResidueSummary[] = [...residues.values()].map(entry => ({ ...entry.residue, atomCount: entry.count, mean: entry.sum / entry.count, min: entry.min, max: entry.max }));
    return pagedResult(services.expandResults, summaries, page => success(`Sampled density at ${count} selected atoms.`, { type: 'density-fit', volumeRef: volume.cell.transform.ref, units: 'sigma', threshold: input.threshold, atomCount: count, mean: sum / count, min, max, fractionBelowThreshold: below / count, residues: page.items }, page.truncation));
}

function cartesianBoundingBox(grid: Grid) {
    const dimensions = grid.cells.space.dimensions;
    const gridBox = Box3D.create(Vec3(), Vec3.ofArray([dimensions[0] - 1, dimensions[1] - 1, dimensions[2] - 1]));
    const cartesian = Box3D.transform(Box3D.zero(), gridBox, Grid.getGridToCartesianTransform(grid));
    return { min: [cartesian.min[0], cartesian.min[1], cartesian.min[2]], max: [cartesian.max[0], cartesian.max[1], cartesian.max[2]] };
}

function volumeStats(plugin: PluginContext, ref: string | undefined) {
    const volume = selectVolume(plugin, ref);
    if (!volume) {
        const available = plugin.managers.volume.hierarchy.current.volumes;
        return available.length === 0
            ? failure('No volume is loaded.', 'Load a density volume, then retry.')
            : failure(ref === void 0 ? 'A volume ref is required when multiple volumes are loaded.' : `Volume '${ref}' is not loaded.`, 'Call get-volume-state and provide one loaded volume ref.');
    }
    const data = volume.cell.obj?.data;
    if (!data) return failure(`Volume '${volume.cell.transform.ref}' is unavailable.`, 'Reload the volume, then retry.');
    const grid = data.grid;
    const robust = Grid.getRobustStats(grid);
    return toMcpResult(success('Volume statistics returned.', { type: 'volume-stats', volumeRef: volume.cell.transform.ref, dimensions: [...grid.cells.space.dimensions], boundingBox: cartesianBoundingBox(grid), stats: grid.stats, robustStats: robust }));
}

/** Volume tools expose only entries present in Mol* VolumeHierarchyManager metadata. */
export function createVolumeTools(plugin: PluginContext, services: WebMcpSafetyServices): readonly ToolDefinition[] {
    return [
        {
            name: 'get-volume-state', description: 'Return loaded volume hierarchy metadata and representation types.', inputSchema: { type: 'object', additionalProperties: false }, annotations: { readOnlyHint: true },
            isAvailable: capabilities => capabilities.hasVolume,
            execute: () => toMcpResult(success('Volume state returned.', { type: 'volumes', volumes: volumes(plugin) }))
        },
        {
            name: 'get-volume-stats', description: 'Return grid dimensions, Cartesian bounds, and basic and robust density statistics.', inputSchema: { type: 'object', properties: { volumeRef: { type: 'string', minLength: 1 } }, additionalProperties: false }, annotations: { readOnlyHint: true },
            isAvailable: capabilities => capabilities.hasVolume,
            execute: input => input.volumeRef === void 0 || isString(input.volumeRef) ? volumeStats(plugin, input.volumeRef) : failure('Invalid volume reference.', 'Provide a loaded volume ref from get-volume-state.')
        },
        {
            name: 'get-density-fit', description: 'Sample one loaded density volume at selected atoms in relative sigma units.', inputSchema: DensityFitSchema, annotations: { readOnlyHint: true },
            isAvailable: capabilities => capabilities.hasVolume && capabilities.hasStructure,
            execute: input => {
                const parsed = parseDensityFitInput(input);
                return parsed ? densityFit(plugin, parsed, services) : failure('Invalid density-fit request.', 'Provide a selector, optional loaded volumeRef, and finite threshold.');
            }
        },
        {
            name: 'set-volume-isovalue', description: 'Set one loaded isosurface representation threshold in sigma or absolute density units.', inputSchema: VolumeIsovalueSchema,
            isAvailable: capabilities => capabilities.hasVolume,
            execute: async input => {
                const representationRef = input.representationRef;
                const kind = input.kind;
                const value = input.value;
                if (!isString(representationRef) || !isIsovalueKind(kind) || !isNumber(value) || value < -1_000_000 || value > 1_000_000) return failure('Invalid volume isovalue.', 'Provide an isosurface representationRef, sigma or absolute kind, and a finite value between -1000000 and 1000000.');
                const representation = plugin.managers.volume.hierarchy.current.volumes.flatMap(volume => volume.representations).find(entry => entry.cell.transform.ref === representationRef);
                if (!representation) return failure(`Representation '${representationRef}' is not loaded.`, 'Choose a representation from get-volume-state.');
                if (!representation.cell.transform.params || representation.cell.transform.params.type.name !== 'isosurface') return failure(`Representation '${representationRef}' is not an isosurface.`, 'Choose an isosurface representation from get-volume-state.');
                const summary = `Set ${kind} isovalue ${value} on ${representationRef}.`;
                const outcome = await runWebMcpMutation({ actor: 'agent', tool: 'set-volume-isovalue', args: { action: 'set-volume-isovalue', target: representationRef, name: kind }, summary }, services, async () => { await setVolumeIsovalue(plugin, representationRef, kind, value); });
                return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'volume-isovalue', representationRef, kind, value })) : failure(outcome.error, outcome.hint);
            }
        },
        {
            name: 'set-volume-representation', description: 'Add an applicable Mol* representation to one loaded volume hierarchy entry.', inputSchema: VolumeRepresentationSchema,
            isAvailable: capabilities => capabilities.hasVolume,
            execute: async input => {
                const ref = input.ref;
                const type = input.type;
                if (!isString(ref) || !isString(type)) return failure('Missing volume reference or representation type.', 'Provide ref and a supported type from get-volume-state.');
                const volume = plugin.managers.volume.hierarchy.current.volumes.find(entry => entry.cell.transform.ref === ref);
                if (!volume) return failure(`Volume '${ref}' is not loaded.`, 'Call get-volume-state and use one of its refs.');
                if (!VolumeHierarchyManager.getRepresentationTypes(plugin, volume).some(entry => entry[0] === type)) return failure(`Volume representation '${type}' is unavailable.`, 'Use a type applicable to this loaded volume.');
                const summary = `Added ${type} representation to ${ref}.`;
                const outcome = await runWebMcpMutation({ actor: 'agent', tool: 'set-volume-representation', args: { action: 'set-volume-representation', target: ref, name: type }, summary }, services, async () => { await plugin.managers.volume.hierarchy.addRepresentation(volume, type); });
                return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'volume', ref, representation: type })) : failure(outcome.error, outcome.hint);
            }
        },
    ];
}
